import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BuildingIcon, KeyIcon, MapPinIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import {
  crewPropertyAddressLine,
  fetchCrewPropertiesForEmployee,
  type CrewPropertyListItem,
} from '../api/crewProperties'
import { resolveEmployeeIdForUser } from '../lib/timeClockActions'
import { translateAccessType } from '../i18n/labels'
import {
  MobileEmptyState,
  MobileListRow,
  MobilePage,
  MobilePageHeader,
} from '../components/mobile'

export function CrewProperties() {
  const { t } = useTranslation('field')
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<CrewPropertyListItem[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!user?.id) {
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        setError(null)
        const empId = await resolveEmployeeIdForUser(user.id)
        if (!empId) {
          if (!cancelled) {
            setItems([])
            setError(t('properties.noEmployee'))
          }
          return
        }
        const rows = await fetchCrewPropertiesForEmployee(empId)
        if (!cancelled) setItems(rows)
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message || t('properties.loadFailed'))
          setItems([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [user?.id, t])

  return (
    <MobilePage>
      <MobilePageHeader
        title={t('properties.title')}
        subtitle={t('properties.subtitle')}
      />

      {loading && (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <MobileEmptyState
          icon={<BuildingIcon className="w-12 h-12" />}
          title={t('properties.empty')}
          description={t('properties.emptyHint')}
        />
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((p) => {
            const address = crewPropertyAddressLine(p)
            return (
              <MobileListRow
                key={p.id}
                title={p.name || address || t('properties.propertyFallback')}
                subtitle={p.name ? address : undefined}
                meta={
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {p.nextJobDate && (
                      <span className="inline-flex items-center gap-1">
                        <MapPinIcon className="w-3.5 h-3.5 text-gray-400" />
                        {t('properties.nextJob', { date: p.nextJobDate })}
                      </span>
                    )}
                    {p.access_type && (
                      <span className="inline-flex items-center gap-1">
                        <KeyIcon className="w-3.5 h-3.5 text-gray-400" />
                        {translateAccessType(p.access_type)}
                      </span>
                    )}
                    <span className="text-gray-400">
                      {t('properties.upcomingJobs', { count: p.jobCount })}
                    </span>
                  </div>
                }
                leading={
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <BuildingIcon className="w-5 h-5" />
                  </span>
                }
                onClick={() => navigate(`/my-properties/${p.id}`)}
              />
            )
          })}
        </div>
      )}
    </MobilePage>
  )
}

export default CrewProperties
